/**
 * Single organization input supporting existing selection or new organization creation.
 * @agentic @benchmark/core
 */

// Components
import {
  Box,
  Group,
  Text,
  Select,
  Stack,
  Switch,
  // MultiSelect, // Commented out - Aliases field is hidden
} from "@mantine/core";
import { IconBuilding, IconUsers, IconBriefcase } from "@tabler/icons-react";
import { FormField } from "@benchmark/ui/components/atomic";
import { FormGroup } from "@benchmark/ui/components/atomic";
import { CardSelect } from "@benchmark/ui/components/atomic";
import { TrimmedTextInput as TextInput } from "@benchmark/ui/components/atomic";

// Constants
import { ORGANIZATION_TIER_SELECT_OPTIONS } from "@benchmark/core/constants";

// Hooks
import { useState, useMemo } from "react";
import { useDebounce } from "@benchmark/ui/hooks";

// Types
import type { UseFormReturnType } from "@mantine/form";
import type { ProviderOrganizationFormData } from "@benchmark/core/types";
import type { ComboboxProps } from "@mantine/core";
import type { EnhancedSelectOption } from "@/types/ui/component-props";
import type { Organization } from "@medplum/fhirtypes";
import styles from "./OrganizationSection.module.css";

/**
 * Props for OrganizationSection component.
 * @agentic @benchmark/core
 */
type OrganizationSectionProps<T extends Record<string, unknown>> = {
  form: UseFormReturnType<T>;
  fieldPath: string;
  existingOrganizations?: Array<{ value: string; label: string }>;
  loading?: boolean;
  disabled?: boolean;
  onExistingOrganizationChange?: (value: string | null) => void;
  title?: string;
  description?: string;
  /** Forwarded to all inner Select components (e.g. to pin dropdown position). */
  comboboxProps?: ComboboxProps;
  // Async search props
  asyncSearch?: boolean;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  searchResults?: Organization[];
  isSearching?: boolean;
  selectedOrganization?: Organization;
};

/**
 * Renders organization select or new organization form with type and tier.
 * @agentic @benchmark/core
 */
export const OrganizationSection = <T extends Record<string, unknown>>({
  form,
  fieldPath,
  existingOrganizations = [],
  loading = false,
  disabled = false,
  onExistingOrganizationChange,
  title,
  description,
  comboboxProps,
  asyncSearch = false,
  searchQuery: controlledSearchQuery,
  onSearchChange: controlledOnSearchChange,
  searchResults,
  isSearching = false,
  selectedOrganization,
}: OrganizationSectionProps<T>) => {
  const organization =
    (form.values[fieldPath] as ProviderOrganizationFormData | undefined) ||
    ({
      isNew: false,
    } as ProviderOrganizationFormData);

  // Internal search query state (used when asyncSearch is true but not controlled)
  const [internalSearchQuery, setInternalSearchQuery] = useState("");
  const searchQuery = controlledSearchQuery ?? internalSearchQuery;
  const setSearchQuery = controlledOnSearchChange ?? setInternalSearchQuery;

  // Debounce for UI feedback
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // Type-safe helper for setting nested field values
  // Type assertion on path string due to Mantine v8 form type inference limitation
  const setNestedFieldValue = <V,>(path: string, value: V): void => {
    (form.setFieldValue as (path: string, value: V) => void)(path, value);
  };

  const toggleNewOrganization = (isNew: boolean) => {
    setNestedFieldValue(`${fieldPath}.isNew`, isNew);
    if (isNew) {
      // Clear existing selection when switching to new
      setNestedFieldValue(`${fieldPath}.existingId`, undefined);
      setNestedFieldValue(`${fieldPath}.existingDisplay`, undefined);
      onExistingOrganizationChange?.(null);
    } else {
      // Clear new organization fields when switching to existing
      setNestedFieldValue(`${fieldPath}.name`, undefined);
      setNestedFieldValue(`${fieldPath}.type`, undefined);
      setNestedFieldValue(`${fieldPath}.tier`, undefined);
      setNestedFieldValue(`${fieldPath}.alias`, undefined);
    }
  };

  // Build options based on async search or static list
  const organizationOptions = useMemo(() => {
    if (asyncSearch) {
      // If searching and have results, show them
      if (
        searchQuery.trim() &&
        Array.isArray(searchResults) &&
        searchResults.length > 0
      ) {
        return searchResults
          .filter((org) => org.id && org.name?.trim())
          .map((org) => ({
            value: org.id as string,
            label: org.name as string,
          }))
          .sort((a, b) => a.label.localeCompare(b.label));
      }

      // If not searching but have a selected value, show it
      if (!searchQuery.trim() && organization.existingId) {
        // Prefer selectedOrganization.name if loaded (most up-to-date display name)
        // But always use organization.existingId as the value to match Select's value prop
        if (selectedOrganization) {
          const org = selectedOrganization as Organization;
          // Only use selectedOrganization if its ID matches the form's existingId
          // This prevents stale data from showing incorrect options
          if (org.id === organization.existingId && org.name?.trim()) {
            return [
              {
                value: organization.existingId,
                label: org.name,
              },
            ];
          }
        }

        // Fallback to existingDisplay from form data (available immediately)
        if (organization.existingDisplay?.trim()) {
          return [
            {
              value: organization.existingId,
              label: organization.existingDisplay,
            },
          ];
        }
      }

      return [];
    }

    // Static mode: use existingOrganizations prop
    return existingOrganizations;
  }, [
    asyncSearch,
    searchQuery,
    searchResults,
    organization.existingId,
    organization.existingDisplay,
    selectedOrganization,
    existingOrganizations,
  ]);

  // UI state for async search
  const hasMinimumChars = searchQuery.trim().length >= 2;
  const debouncedHasMinimumChars = debouncedSearchQuery.trim().length >= 2;
  const isPendingSearch =
    hasMinimumChars && !debouncedHasMinimumChars && !isSearching;

  const handleExistingOrganizationChange = (value: string | null) => {
    setNestedFieldValue(`${fieldPath}.existingId`, value || undefined);

    // Find display name from appropriate source
    let displaySet = false;

    if (asyncSearch) {
      // Check search results first
      if (Array.isArray(searchResults) && value) {
        const selectedOrg = searchResults.find((org) => org.id === value);
        if (selectedOrg?.name) {
          setNestedFieldValue(`${fieldPath}.existingDisplay`, selectedOrg.name);
          displaySet = true;
        }
      }

      // Fallback to selectedOrganization
      if (
        !displaySet &&
        selectedOrganization &&
        selectedOrganization.resourceType === "Organization" &&
        selectedOrganization.id === value &&
        selectedOrganization.name
      ) {
        setNestedFieldValue(
          `${fieldPath}.existingDisplay`,
          selectedOrganization.name,
        );
        displaySet = true;
      }
    } else {
      // Static mode: use existingOrganizations
      const selectedOption = existingOrganizations.find(
        (org) => org.value === value,
      );
      if (selectedOption?.label) {
        setNestedFieldValue(
          `${fieldPath}.existingDisplay`,
          selectedOption.label,
        );
        displaySet = true;
      }
    }

    // Clear display if not found
    if (!displaySet) {
      setNestedFieldValue(`${fieldPath}.existingDisplay`, undefined);
    }

    onExistingOrganizationChange?.(value);
  };

  // Transform SERVICE_PROVIDER_TYPE_OPTIONS to CardSelect options with icons
  const typeCardOptions: EnhancedSelectOption[] = [
    {
      value: "prov",
      label: "Healthcare Provider",
      icon: <IconBuilding size={24} />,
    },
    {
      value: "cg",
      label: "Community Group",
      icon: <IconUsers size={24} />,
    },
    {
      value: "private-practice",
      label: "Private Practice",
      icon: <IconBriefcase size={24} />,
    },
  ];

  return (
    <Box>
      {(title || description) && (
        <Box mb="sm">
          {title && (
            <Text size="sm" fw={500}>
              {title}
            </Text>
          )}
          {description && (
            <Text size="xs" c="dimmed">
              {description}
            </Text>
          )}
        </Box>
      )}

      <Stack gap="sm">
        <Group justify="space-between" align="center" gap="sm">
          <Box className={styles.selectGrow}>
            <Select
              placeholder={
                asyncSearch
                  ? "Search organizations..."
                  : loading
                    ? "Loading..."
                    : "Select existing organization"
              }
              data={organizationOptions}
              searchable
              clearable
              disabled={
                // Don't disable during async search - show loading state in dropdown instead
                (asyncSearch ? false : loading) ||
                organization.isNew ||
                disabled
              }
              value={organization.existingId || null}
              onChange={handleExistingOrganizationChange}
              comboboxProps={comboboxProps}
              {...(asyncSearch && {
                onSearchChange: setSearchQuery,
                filter: ({ options }) => options, // Bypass client-side filtering
                nothingFoundMessage: !hasMinimumChars
                  ? "Type at least 2 characters to search"
                  : isPendingSearch || isSearching
                    ? "Searching..."
                    : Array.isArray(searchResults) && searchResults.length === 0
                      ? "No organizations found"
                      : "Type at least 2 characters to search",
              })}
            />
          </Box>
          <Switch
            size="sm"
            label="New Organization"
            checked={organization.isNew}
            onChange={(e) => toggleNewOrganization(e.currentTarget.checked)}
            disabled={disabled}
            classNames={{
              root: styles.newOrganizationToggleRoot,
              label: styles.newOrganizationToggleLabel,
              track: styles.newOrganizationToggleTrack,
            }}
          />
        </Group>

        {organization.isNew && (
          <Stack gap="sm">
            <FormField label="Organization Name" required>
              <TextInput
                placeholder="Enter organization name"
                {...form.getInputProps(`${fieldPath}.name`)}
              />
            </FormField>

            <FormField label="Organization Type" required>
              <CardSelect
                data={typeCardOptions}
                value={organization.type || null}
                onChange={(value) =>
                  setNestedFieldValue(`${fieldPath}.type`, value as string)
                }
                columns={3}
                error={!!form.errors[`${fieldPath}.type`]}
              />
            </FormField>

            <FormGroup>
              <FormField label="Partnership Tier">
                <Select
                  placeholder="Select partnership tier"
                  data={ORGANIZATION_TIER_SELECT_OPTIONS}
                  searchable
                  clearable
                  comboboxProps={comboboxProps}
                  {...form.getInputProps(`${fieldPath}.tier`)}
                />
              </FormField>

              {/* <FormField label="Aliases">
                <MultiSelect
                  placeholder="Enter aliases"
                  data={organization.alias || []}
                  searchable
                  value={organization.alias || []}
                  onChange={(values: string[]) =>
                    setNestedFieldValue(`${fieldPath}.alias`, values)
                  }
                />
              </FormField> */}
            </FormGroup>
          </Stack>
        )}
      </Stack>
    </Box>
  );
};
